import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

import { IRhytSortingData, ITaskEditorProps, ITaskEditorState } from "../../../../../../common/types";

import Icon from "../../../../../icon";
import { setItem, hasItem } from "../../../../../cookie";

import { Range, Select, Checkbox } from "../../../../Form";
import Tooltip from "../../../../Tooltip";

import EditorBar, {
    FlexLeft,
    FlexRight,
    Center,
    Changer,
    ChangerPopup,
} from "../../../EditorBar";
import Wrapper from "../../../Wrapper";
import ViewContainer from "../../../ViewContainer";

import RhytCardEditor from "./RhytCardEditor";
import { SelectWrapper, ScaleAlert, TooltipLabel } from "./styles";
import messages from "./messages";
import { ICheckboxEvent, ISelectEvent } from "../../../../../types";
import ChangeColor from "../../Common/ChangeColor";
import ChangeTempo from "../../Common/ChangeTempo";
import FormattedHtmlMessage from "../../../../../../common/helpers/FormattedHtmlMessage";

class RhytSortingEditor extends Component<ITaskEditorProps, ITaskEditorState> {
    state: ITaskEditorState = {
        openChanger: undefined,
        playable: false,
    };
    popup?: HTMLDivElement;
    changeScale = (val: number) => {
        const { taskIndex, editTask } = this.props;
        editTask({ taskIndex, changingProp: "scale", val });
    };

    changeScaleStop = () => {
        const { plan, taskIndex, post } = this.props;
        const { scale } = plan.tasks[taskIndex].data as IRhytSortingData;
        post("task-scale", {
            planId: plan._id,
            index: taskIndex,
            scale,
        });
    };

    changeFollow = (e: ICheckboxEvent) => {
        const { plan, taskIndex, post, editTask } = this.props;
        let follow = e.target.checked;
        editTask({ taskIndex, changingProp: "follow", val: follow });
        post("rhyt-sorting-follow", {
            planId: plan._id,
            index: taskIndex,
            follow,
        });
    };

    changeOpen = (changer: string) => {
        setTimeout(() => {
            if (this.state.openChanger === changer) {
                this.setState({ openChanger: undefined });
                window.removeEventListener("click", this.isClickedOutside);
            } else {
                this.setState({ openChanger: changer });
                setTimeout(() => {
                    window.addEventListener("click", this.isClickedOutside);
                });
            }
        });
    };
    isClickedOutside = (e: MouseEvent) => {
        if (!this.popup!.contains(e.target as Node)) {
            this.setState({ openChanger: undefined });
            window.removeEventListener("click", this.isClickedOutside);
        }
    };
    changeMetreDivision = (e: ISelectEvent) => {
        const { plan, taskIndex, editTask, post } = this.props;
        editTask({ taskIndex, changingProp: "metreDivision", val: e.target.value });
        post("rhyt-sorting-change-metre-division", {
            planId: plan._id,
            index: taskIndex,
            metreDivision: e.target.value,
        });
    };
    componentWillUnmount() {
        window.removeEventListener("click", this.isClickedOutside);
    }
    setPopupRef = (t: HTMLDivElement) => (this.popup = t)
    render() {
        const {
            plan,
            taskIndex,
            publicView,
            viewProps,
            playable,
            toggleInlineEditor,
            inlineEditorOpen,
            isScaleAlert,
        } = this.props;
        const { formatMessage } = this.props.intl!;
        const { openChanger } = this.state;
        let task = plan.tasks[taskIndex];
        const data = task.data as IRhytSortingData;
        const forwardProps = {
            plan,
            taskIndex,
            openChanger,
            handleChangeOpen: this.changeOpen,
            setPopupRef: this.setPopupRef
        }
        return (
            <div>
                {inlineEditorOpen && (
                    <ViewContainer viewProps={viewProps}>
                        <Wrapper>
                            <RhytCardEditor {...this.props} />
                        </Wrapper>
                    </ViewContainer>
                )}
                {!publicView && (
                    <EditorBar>
                        <FlexLeft>
                            {task && task.data && (
                                <Changer active={openChanger === "scale"}>
                                    <div
                                        onClick={e => {
                                            e.stopPropagation();
                                            this.changeOpen("scale");
                                        }}
                                    >
                                        <Icon i="scale" />
                                        {isScaleAlert && (
                                            <Tooltip
                                                light
                                                label={
                                                    <TooltipLabel>
                                                        <FormattedHtmlMessage {...messages.scaleAlert} />
                                                    </TooltipLabel>
                                                }
                                                flipped
                                            >
                                                <ScaleAlert>
                                                    <Icon i="alert" />
                                                </ScaleAlert>
                                            </Tooltip>
                                        )}
                                    </div>
                                    {openChanger === "scale" && (
                                        <ChangerPopup setRef={t => (this.popup = t)}>
                                            <Range
                                                scale={1}
                                                value={data.scale || 1.2}
                                                onChange={this.changeScale}
                                                onStop={this.changeScaleStop}
                                                min={0.5}
                                                max={3}
                                                step={0.05}
                                                label={<FormattedMessage {...messages.scaling} />}
                                            />
                                        </ChangerPopup>
                                    )}
                                </Changer>
                            )}
                            {task && task.data && (
                                <SelectWrapper>
                                    <div>
                                        <FormattedMessage {...messages.beatDivision} />:
                                    </div>
                                    <Select
                                        value={data.metreDivision || "even"}
                                        name="metreDivision"
                                        options={[
                                            {
                                                label: formatMessage(messages.even),
                                                value: "even",
                                            },
                                            {
                                                label: formatMessage(messages.odd),
                                                value: "odd",
                                            },
                                            {
                                                label: formatMessage(messages.variable),
                                                value: "variable",
                                            },
                                        ]}
                                        onChange={this.changeMetreDivision}
                                        noDelete
                                        dark
                                    ></Select>
                                </SelectWrapper>
                            )}
                        </FlexLeft>
                        <Center
                            disabled={inlineEditorOpen && !playable}
                            onClick={() => {
                                toggleInlineEditor(!inlineEditorOpen);
                                setItem("rhytSortingDone", "1", 60 * 60 * 24 * 365, "/");
                            }}
                            className={cn(!(inlineEditorOpen && !playable) && !hasItem("rhytSortingDone") && "pulsate")}
                        >
                            {inlineEditorOpen ? (
                                <FormattedMessage {...messages.stopEdit} />
                            ) : (
                                <FormattedMessage {...messages.editCards} />
                            )}
                        </Center>
                        <FlexRight>
                            {task && task.data && (
                                <ChangeColor
                                    {...forwardProps}
                                    color={data?.color}
                                />
                            )}
                            {task && task.data && (
                                <Changer>
                                    <Checkbox
                                        dark
                                        label={<FormattedMessage {...messages.following} />}
                                        checked={data.follow === undefined ? true : data.follow}
                                        onChange={this.changeFollow}
                                    ></Checkbox>
                                </Changer>
                            )}
                            {task?.data && <ChangeTempo {...forwardProps} />}
                        </FlexRight>
                    </EditorBar>
                )}
            </div>
        );
    }
}

export default RhytSortingEditor;
